import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  CatalogCategory,
  CatalogCategoryUsage,
  CatalogCustomField,
  CatalogCycle,
  CatalogEntry,
  CatalogFormContext,
  CatalogOrderList,
  CatalogProduct,
  CatalogRowUsage,
  CatalogSettings,
  FollowupOption,
  SaveCatalogOrderInput,
  UpdateCatalogSettingsInput,
} from "@school-sales-ai/shared";
import { api, getCatalogContextApi } from "@/lib/api";

/** What one row of each catalog list holds. */
export type CatalogRowValue = {
  products: CatalogProduct;
  cycles: CatalogCycle;
  customFields: CatalogCustomField;
  categories: CatalogCategory;
};

const LIST_PATH: Record<CatalogOrderList, string> = {
  products: "products",
  cycles: "cycles",
  customFields: "custom-fields",
  categories: "categories",
};

type WriteOptions = { keepalive?: boolean };

/**
 * The per-row catalog endpoints. The editor saves each edit the moment it is
 * made, so these are plain functions its hook calls from its own save queue —
 * not mutations, whose one-in-flight model is the wrong shape for many rows
 * saving independently.
 */
export const catalogRowApi = {
  create<L extends CatalogOrderList>(
    list: L,
    body: unknown,
    options: WriteOptions = {},
  ): Promise<CatalogRowValue[L] & { id: string }> {
    return api.post(`/crm/catalog/${LIST_PATH[list]}`, body, options);
  },
  update<L extends CatalogOrderList>(
    list: L,
    id: string,
    body: unknown,
    options: WriteOptions = {},
  ): Promise<CatalogRowValue[L] & { id: string }> {
    return api.patch(`/crm/catalog/${LIST_PATH[list]}/${id}`, body, options);
  },
  deactivate(list: CatalogOrderList, id: string): Promise<unknown> {
    return api.post(`/crm/catalog/${LIST_PATH[list]}/${id}/deactivate`, {});
  },
  reactivateProduct(id: string): Promise<CatalogProduct & { id: string }> {
    return api.post(`/crm/catalog/products/${id}/reactivate`, {});
  },
  usage(list: CatalogOrderList, id: string): Promise<CatalogRowUsage> {
    return api.get(`/crm/catalog/${LIST_PATH[list]}/${id}/usage`);
  },
  categoryUsage(id: string): Promise<CatalogCategoryUsage> {
    return api.get(`/crm/catalog/categories/${id}/usage`);
  },
  updateSettings(input: UpdateCatalogSettingsInput): Promise<CatalogSettings> {
    return api.patch("/crm/catalog/settings", input);
  },
  saveOrder(input: SaveCatalogOrderInput): Promise<void> {
    return api.put("/crm/catalog/order", input);
  },
};

/** How many active products a category holds — what deactivating it sends to the root. */
export function useCatalogCategoryUsage(
  id: string | null,
): ReturnType<typeof useQuery<CatalogCategoryUsage>> {
  return useQuery<CatalogCategoryUsage>({
    queryKey: ["catalog-category-usage", id],
    queryFn: () => catalogRowApi.categoryUsage(id ?? ""),
    enabled: id !== null,
    staleTime: 0,
  });
}

/** Prospects per cycle, for the dialog that deactivates them all at once. */
export function useCatalogCyclesUsage(
  cycleIds: string[],
  enabled: boolean,
): Array<number | undefined> {
  const results = useQueries({
    queries: cycleIds.map((id) => ({
      queryKey: ["catalog-row-usage", "cycles", id],
      queryFn: () => catalogRowApi.usage("cycles", id),
      enabled,
      staleTime: 0,
    })),
  });
  return results.map((r) => r.data?.leads);
}

/**
 * How many prospects a row carries — read when the deactivation dialog opens,
 * so the number is current rather than whatever the page loaded with.
 */
export function useCatalogRowUsage(
  list: CatalogOrderList,
  id: string | null,
): ReturnType<typeof useQuery<CatalogRowUsage>> {
  return useQuery<CatalogRowUsage>({
    queryKey: ["catalog-row-usage", list, id],
    queryFn: () => catalogRowApi.usage(list, id ?? ""),
    enabled: id !== null,
    staleTime: 0,
  });
}

export function useCatalogContext(): ReturnType<
  typeof useQuery<CatalogFormContext>
> {
  return useQuery<CatalogFormContext>({
    queryKey: ["catalog-context"],
    queryFn: getCatalogContextApi,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCatalogQuery(): ReturnType<typeof useQuery<CatalogEntry>> {
  return useQuery<CatalogEntry>({
    queryKey: ["catalog"],
    queryFn: () => api.get<CatalogEntry>("/crm/catalog"),
  });
}

export function useSuggestInitialOptionsMutation(): ReturnType<
  typeof useMutation<FollowupOption[], Error, void>
> {
  return useMutation<FollowupOption[], Error, void>({
    mutationFn: () =>
      api.post<FollowupOption[]>("/crm/catalog/suggest-initial-options", {}),
    onError: (error) => {
      toast.error(error.message ?? "Error al sugerir opciones.");
    },
  });
}

/**
 * Deletes an INACTIVE product nothing uses. The server re-checks both; its 409
 * says why in the admin's words, so the toast just relays it.
 */
export function useDeleteInactiveProduct(): ReturnType<
  typeof useMutation<void, Error, string>
> {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (productId) => api.delete<void>(`/crm/catalog/products/${productId}`),
    onSuccess: () => {
      toast.success("Programa eliminado.");
      void queryClient.invalidateQueries({ queryKey: ["catalog"] });
    },
    onError: (error) => {
      toast.error(error.message || "No se pudo eliminar.");
    },
  });
}
